import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import { FormikProvider, setNestedObjectValues, useFormik } from 'formik';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import useDeclarations from 'hooks/useDeclarations';
import useFormUtils from 'hooks/useFormUtils';
import useProducts from 'hooks/useProducts';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Declaration } from 'store/declarations/declaration';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { useDeclarationContext } from 'views/declarations/common/DeclarationContext';
import { useProductContext } from '../../context/ProductContext';
import useGetDeclarationMapValues from 'hooks/useGetDeclarationMapValues';
import UkNctsProductsSection from 'views/declarations/uk/ncts/UkNctsProductsSection';
import IrelandH1ProductsSection from 'views/declarations/ireland/import/h1/IrelandH1ProductsSection';
import IrelandH7ProductsSection from 'views/declarations/ireland/import/h7/IrelandH7ProductsSection';
import IrelandExportProductsSection from 'views/declarations/ireland/export/IrelandExportProductsSection';
import IrelandTsdProductsSection from 'views/declarations/ireland/tsd/IrelandTsdProductsSection';
import IrelandNctsProductsSection from 'views/declarations/ireland/ncts/IrelandNctsProductsSection';
import IrelandEtdProductsSection from 'views/declarations/ireland/etd/IrelandEtdProductsSection';
import IrelandI1ProductSection from 'views/declarations/ireland/import/i1/IrelandI1ProductsSection';
import UkProductsSection from 'views/declarations/uk/UkProductsSection';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { TaricDetailsType } from 'views/declarations/common/taric-codes/TaricCodesCard';
import { updateIrelandH1ImportTaricCode } from 'views/declarations/common/taric-codes/utils';
import ICS2ENSProductsForm from 'views/declarations/eu/ics2/ICS2ENSProductForm';

interface Props {
    declaration?: Declaration | null | undefined;
}

const Product = ({ declaration: declarationProp }: Props) => {
    const context = useProductView();
    const { productId, declarationId } = useParams<string>();
    const { getDeclarationProduct, product } = useProducts({ productId });
    const { formType, internalType, countryLowerCase: country } = useFormUtils();
    const { setProductId } = useProductContext();
    const { transformData } = useGetDeclarationMapValues();

    const { template } = useTemplateContext();
    const { setForm } = useDeclarationContext();
    useEffect(() => {
        if (template) return;
        setForm?.('product');
    }, [setForm, template]);

    const mockFormik = useFormik({
        initialValues: {},
        onSubmit: () => {},
    });
    const formik = useMemo(() => context?.productsFormik || mockFormik, [context?.productsFormik, mockFormik]);

    const { declaration: _declaration } = useDeclarations();
    const declaration = declarationProp ?? _declaration;

    const { declarationErrors: formDeclarationErrors } = useDeclarationFormErrors();

    useEffect(() => {
        if (isEmpty(formDeclarationErrors.items)) return;

        context?.productsFormik?.validateForm().then((v) => {
            context?.productsFormik?.setTouched(setNestedObjectValues(v, true));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setUpContextAndFormik = useCallback(
        (product: any, productId: string | undefined) => {
            setProductId?.(productId);

            if (transformData?.product?.forClient) {
                const transformedProduct = transformData?.product?.forClient?.(product);
                formik.setValues(transformedProduct ?? {});
            }
        },
        [formik, setProductId, transformData?.product]
    );

    const fetchProduct = useCallback(
        async (declarationId: string, productId: string) => {
            const fetchedProduct = await getDeclarationProduct({
                declarationId,
                productId,
                formType,
                internalType,
                country,
            });
            setUpContextAndFormik(fetchedProduct, productId);
        },
        [country, formType, getDeclarationProduct, internalType, setUpContextAndFormik]
    );

    useEffect(() => {
        if (declarationId && productId && productId !== 'new') {
            if (product?.id !== productId && !context?.cancelFetchProduct) {
                fetchProduct(declarationId, productId);
            } else {
                setUpContextAndFormik(product, productId);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context?.cancelFetchProduct, declarationId, productId]);

    const container = useMemo(() => {
        if (!declaration || !formik) return null;

        switch (declaration.declarationExternalEntity) {
            case DeclarationExternalEntity.REVENUE: {
                if (declaration.declarationInternalType === DeclarationInternalType.IMPORT) {
                    if (declaration.irelandImportDeclaration) {
                        if (declaration.irelandImportDeclaration.messageType === 'I1') {
                            return (
                                <IrelandI1ProductSection
                                    formik={formik}
                                    toggleHelp={context?.toggleHelp}
                                    viewOnly={context?.viewOnly}
                                    productTemplate={context?.productTemplate}
                                    selectCard={context?.selectCard}
                                    country={DeclarationCountry.IRELAND}
                                    detailsType={
                                        context?.productTemplate ? TaricDetailsType.BOTH : TaricDetailsType.IMPORT
                                    }
                                    updateTaricCode={(code) => updateIrelandH1ImportTaricCode(formik, code)}
                                />
                            );
                        } else {
                            return (
                                <IrelandH1ProductsSection
                                    formik={formik}
                                    toggleHelp={context?.toggleHelp}
                                    viewOnly={context?.viewOnly}
                                    productTemplate={context?.productTemplate}
                                    selectCard={context?.selectCard}
                                />
                            );
                        }
                    } else {
                        return (
                            <IrelandH7ProductsSection
                                formik={formik}
                                toggleHelp={context?.toggleHelp}
                                viewOnly={context?.viewOnly}
                            />
                        );
                    }
                } else if (
                    declaration.declarationInternalType === DeclarationInternalType.EXPORT ||
                    declaration.declarationInternalType === DeclarationInternalType.ARRIVAL
                ) {
                    return <IrelandExportProductsSection formik={formik} />;
                } else if (declaration.declarationInternalType === DeclarationInternalType.TEMPORARY) {
                    return <IrelandTsdProductsSection formik={formik} />;
                } else if (DeclarationInternalType.NCTS === declaration.declarationInternalType) {
                    return <IrelandNctsProductsSection formik={formik} />;
                } else if (declaration.declarationInternalType === DeclarationInternalType.ETD) {
                    return <IrelandEtdProductsSection formik={formik} />;
                } else return <></>;
            }
            case DeclarationExternalEntity.CDS:
                return (
                    <UkProductsSection formik={formik} toggleHelp={context?.toggleHelp} viewOnly={context?.viewOnly} />
                );
            case DeclarationExternalEntity.HMRC:
                return <UkNctsProductsSection formik={formik} />;
            case DeclarationExternalEntity.ICS2:
                return (
                    <ICS2ENSProductsForm
                        formik={formik}
                        // TODO:
                        // toggleHelp={context?.toggleHelp}
                        viewOnly={context?.viewOnly}
                    />
                );

            default:
                return null;
        }
    }, [declaration, context, formik]);

    if (!formik) return null;

    return <FormikProvider value={formik}>{container}</FormikProvider>;
};
export default Product;

export function useProductView() {
    return useOutletContext<
        | {
              productsFormik?: ReturnType<typeof useFormik>; // FIXME use correct type
              toggleHelp?: (refNumber: string | number) => void;
              viewOnly?: boolean;
              productTemplate?: boolean;
              selectCard?: (id: string) => void;
              cancelFetchProduct?: boolean;
          }
        | undefined
    >();
}
